import { useState, useEffect, useRef } from "react";

// Reusable Penguin SVG Component
function Penguin({ isMoving, scale = 1 }) {
  return (
    <svg
      width={60 * scale}
      height={60 * scale}
      viewBox="0 0 60 60"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Penguin body */}
      <ellipse
        cx="30"
        cy="35"
        rx="18"
        ry="20"
        fill="#000"
        className={isMoving ? "penguin-body-wobble" : ""}
      />

      {/* White belly */}
      <ellipse
        cx="30"
        cy="37"
        rx="12"
        ry="15"
        fill="#fff"
      />

      {/* Penguin head */}
      <circle
        cx="30"
        cy="20"
        r="15"
        fill="#000"
      />

      {/* White face */}
      <ellipse
        cx="30"
        cy="22"
        rx="10"
        ry="11"
        fill="#fff"
      />

      {/* Eyes - always happy */}
      <g className={isMoving ? "penguin-eyes-blink" : ""}>
        <path
          d="M 24 20 Q 24 22 24 22"
          stroke="#000"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M 36 20 Q 36 22 36 22"
          stroke="#000"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
      </g>

      {/* Rosy cheeks */}
      <circle cx="22" cy="25" r="3" fill="#ffb6c1" opacity="0.6" />
      <circle cx="38" cy="25" r="3" fill="#ffb6c1" opacity="0.6" />

      {/* Beak */}
      <ellipse
        cx="30"
        cy="26"
        rx="3"
        ry="2.5"
        fill="#ffa500"
      />

      {/* Feet */}
      <ellipse
        cx="24"
        cy="54"
        rx="4"
        ry="3"
        fill="#ffa500"
        className={isMoving ? "penguin-feet-waddle-left" : ""}
      />
      <ellipse
        cx="36"
        cy="54"
        rx="4"
        ry="3"
        fill="#ffa500"
        className={isMoving ? "penguin-feet-waddle-right" : ""}
      />

      {/* Wings */}
      <ellipse
        cx="15"
        cy="35"
        rx="5"
        ry="12"
        fill="#000"
        transform="rotate(-20 15 35)"
        className={isMoving ? "penguin-wing-left" : ""}
      />
      <ellipse
        cx="45"
        cy="35"
        rx="5"
        ry="12"
        fill="#000"
        transform="rotate(20 45 35)"
        className={isMoving ? "penguin-wing-right" : ""}
      />
    </svg>
  );
}

function HeartBurst() {
  const hearts = Array.from({ length: 6 }, (_, i) => {
    const angle = -30 + (i * 30); // spread from -30 to 120 degrees
    const dist = 20 + Math.random() * 15;
    const tx = Math.cos((angle * Math.PI) / 180) * dist;
    const ty = -Math.abs(Math.sin((angle * Math.PI) / 180) * dist) - 10;
    const delay = i * 0.25;
    const size = 9 + Math.random() * 5;
    return (
      <svg
        key={i}
        width={size}
        height={size}
        viewBox="0 0 10 10"
        style={{
          position: "absolute",
          left: "50%",
          top: 0,
          marginLeft: -size / 2,
          marginTop: -size / 2,
          animation: `heart-float 1.2s ${delay}s ease-out both`,
          "--tx": `${tx}px`,
          "--ty": `${ty}px`,
        }}
      >
        <path
          d="M5 8 C5 8 1 5 1 3 C1 1.3 2.3 0 4 0 C4.8 0 5 0.5 5 0.5 C5 0.5 5.2 0 6 0 C7.7 0 9 1.3 9 3 C9 5 5 8 5 8Z"
          fill="#ff69b4"
        />
      </svg>
    );
  });
  return <>{hearts}</>;
}

function PenguinCursor() {
  const [isStartled, setIsStartled] = useState(false);

  const firstRef = useRef(null);
  const secondRef = useRef(null);

  const targetPos = useRef({ x: -100, y: -100 });
  const pos1 = useRef({ x: -100, y: -100 });
  const pos2 = useRef({ x: -150, y: -150 });
  const moving1 = useRef(false);
  const moving2 = useRef(false);
  const startledTimer = useRef(null);

  // Input handlers
  useEffect(() => {
    const set = (x, y) => { targetPos.current = { x, y }; };

    const handleMouseMove = (e) => set(e.clientX, e.clientY);
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) set(e.touches[0].clientX, e.touches[0].clientY);
    };
    const handleTouchStart = (e) => {
      const t = e.target;
      const interactive = t.tagName === 'A' || t.tagName === 'BUTTON' ||
        t.tagName === 'INPUT' || t.onclick ||
        t.closest('a, button, input, select, textarea');
      if (!interactive && e.touches.length > 0) set(e.touches[0].clientX, e.touches[0].clientY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchstart", handleTouchStart);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchstart", handleTouchStart);
    };
  }, []);

  // Single animation loop — direct DOM updates, no setState per frame
  useEffect(() => {
    let raf;
    const animate = () => {
      const p = pos1.current;
      const t = targetPos.current;
      const dx1 = t.x - p.x;
      const dy1 = t.y - p.y;
      const d1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);

      if (d1 > 1) {
        pos1.current = { x: p.x + dx1 * 0.1, y: p.y + dy1 * 0.1 };
        moving1.current = true;
      } else {
        moving1.current = false;
      }

      const s = pos2.current;
      const dx2 = pos1.current.x - s.x;
      const dy2 = pos1.current.y - s.y;
      const d2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

      if (d2 > 1) {
        pos2.current = { x: s.x + dx2 * 0.04, y: s.y + dy2 * 0.04 };
        moving2.current = true;
      } else {
        moving2.current = false;
      }

      // Hearts trigger (only setState that matters)
      if (d2 < 80 && d2 > 0 && !startledTimer.current) {
        setIsStartled(true);
        startledTimer.current = setTimeout(() => {
          setIsStartled(false);
          startledTimer.current = null;
        }, 2000);
      }

      // Direct DOM updates
      const el1 = firstRef.current;
      const el2 = secondRef.current;
      if (el1) {
        const rot1 = Math.max(-10, Math.min(10, (t.x - pos1.current.x) * 0.05));
        el1.style.left = (pos1.current.x - 30) + "px";
        el1.style.top = (pos1.current.y - 30) + "px";
        el1.style.transform = `rotate(${rot1}deg)`;
      }
      if (el2) {
        const rot2 = Math.max(-10, Math.min(10, (pos1.current.x - pos2.current.x) * 0.05));
        el2.style.left = (pos2.current.x - 24) + "px";
        el2.style.top = (pos2.current.y - 24) + "px";
        el2.style.transform = `rotate(${rot2}deg)`;
      }

      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <>
      {/* First Penguin - follows cursor */}
      <div
        ref={firstRef}
        style={{
          position: "fixed",
          left: -100,
          top: -100,
          width: "60px",
          height: "60px",
          pointerEvents: "none",
          zIndex: 9999,
        }}
      >
        <Penguin isMoving={true} scale={1} />
        {isStartled && <HeartBurst />}
      </div>

      {/* Second Penguin - follows first penguin (smaller and slower) */}
      <div
        ref={secondRef}
        style={{
          position: "fixed",
          left: -150,
          top: -150,
          width: "48px",
          height: "48px",
          pointerEvents: "none",
          zIndex: 10000,
        }}
      >
        <Penguin isMoving={true} scale={0.8} />
      </div>

      <style>{`
        @keyframes wobble {
          0%, 100% { transform: translateY(0) scaleY(1); }
          50% { transform: translateY(-2px) scaleY(1.05); }
        }

        @keyframes waddleLeft {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-1px); }
        }

        @keyframes waddleRight {
          0%, 100% { transform: translateY(-1px); }
          50% { transform: translateY(0); }
        }

        @keyframes flapLeft {
          0%, 100% { transform: rotate(-20deg); }
          50% { transform: rotate(-35deg); }
        }

        @keyframes flapRight {
          0%, 100% { transform: rotate(20deg); }
          50% { transform: rotate(35deg); }
        }

        @keyframes heart-float {
          0% { transform: translate(0, 0) scale(0); opacity: 1; }
          20% { transform: translate(calc(var(--tx) * 0.3), calc(var(--ty) * 0.3)) scale(1); opacity: 1; }
          100% { transform: translate(var(--tx), var(--ty)) scale(0.5); opacity: 0; }
        }

        @keyframes blink {
          0%, 90%, 100% { opacity: 1; }
          95% { opacity: 0.2; }
        }

        .penguin-body-wobble {
          animation: wobble 0.5s ease-in-out infinite;
          transform-origin: center bottom;
        }

        .penguin-feet-waddle-left {
          animation: waddleLeft 0.4s ease-in-out infinite;
        }

        .penguin-feet-waddle-right {
          animation: waddleRight 0.4s ease-in-out infinite;
        }

        .penguin-wing-left {
          animation: flapLeft 0.5s ease-in-out infinite;
          transform-origin: 15px 35px;
        }

        .penguin-wing-right {
          animation: flapRight 0.5s ease-in-out infinite;
          transform-origin: 45px 35px;
        }

        .penguin-eyes-blink {
          animation: blink 3s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}

export default PenguinCursor;
